import {
  Business,
  Contact,
  Household,
  LexicalText,
  Mention,
  MinimalUser,
  StorageAttachmentMeta,
  Tag,
  TaskAttachment,
  TaskPriority,
  Team,
  User,
} from "@/schema"
import { create } from "zustand"

import type { MonthlyRuleType, Weekday, WeekdayOrdinal } from "@/components/tasks/utils/monthly-recurrence"

export interface TaskDraft {
  title?: LexicalText
  description?: LexicalText
  date: Date | undefined
  assignee: User | null
  assignedTeam: Team | null
  assignedToCustomFieldId: string | null
  collaborators: MinimalUser[]
  createMore: boolean
  allDay: boolean
  initialCollaboratorsSet: boolean
  household: Household | null
  contact: Contact | null
  business: Business | null
  recurrenceEnabled: boolean
  recurrenceIntervalValue: number | null
  recurrenceIntervalUnit: "days" | "weeks" | "months" | "years" | null
  recurrenceMonthlyRuleType: MonthlyRuleType
  recurrenceWeekday: Weekday | null
  recurrenceWeekdayOrdinal: WeekdayOrdinal | null
  recurrenceStart?: Date
  recurrenceEnd?: Date
  priority: TaskPriority | null
  label: Tag | null
  mentions: Mention[]
  files: File[]
  templateAttachments: TaskAttachment[]
  templateStorageAttachments: StorageAttachmentMeta[]
  bookId: string | null
  taskTemplateId: string | null
  projectId: string | null
  projectMilestoneId: string | null
  isPrivate: boolean
  // Snapshot of the values this draft was seeded with, captured once at creation so
  // dirty-checking survives a pin/reopen (which rebuilds the dialog without its props).
  baselineDateMs: number | null
  baselineHouseholdId: string | null
  baselineContactId: string | null
  baselineBusinessId: string | null
  baselineMentionKey: string
  baselineAssigneeId: string | null
  baselineBookId: string | null
}

interface TaskStore {
  drafts: Record<string, TaskDraft>
  createDraft: () => string
  updateDraft: (draftId: string, updates: Partial<TaskDraft>) => void
  removeDraft: (draftId: string) => void
}

const DEFAULT_DRAFT: TaskDraft = {
  date: new Date(new Date().setHours(0, 0, 0, 0)),
  assignee: null,
  assignedTeam: null,
  assignedToCustomFieldId: null,
  collaborators: [],
  createMore: false,
  allDay: true,
  initialCollaboratorsSet: false,
  household: null,
  contact: null,
  business: null,
  recurrenceEnabled: false,
  recurrenceIntervalValue: null,
  recurrenceIntervalUnit: null,
  recurrenceMonthlyRuleType: "calendar_day",
  recurrenceWeekday: null,
  recurrenceWeekdayOrdinal: null,
  recurrenceStart: undefined,
  recurrenceEnd: undefined,
  priority: null,
  label: null,
  mentions: [],
  files: [],
  templateAttachments: [],
  templateStorageAttachments: [],
  bookId: null,
  taskTemplateId: null,
  projectId: null,
  projectMilestoneId: null,
  isPrivate: false,
  baselineDateMs: null,
  baselineHouseholdId: null,
  baselineContactId: null,
  baselineBusinessId: null,
  baselineMentionKey: "",
  baselineAssigneeId: null,
  baselineBookId: null,
}

export const useTaskStore = create<TaskStore>()((set) => ({
  drafts: {},

  createDraft: () => {
    const secondPrecisionId = Math.round(Date.now() / 2000)
    const draftId = secondPrecisionId.toString()
    set((state) => {
      return {
        drafts: { ...state.drafts, [draftId]: { ...DEFAULT_DRAFT } },
      }
    })
    return draftId
  },

  updateDraft: (draftId: string, updates: Partial<TaskDraft>) =>
    set((state) => {
      const draft = state.drafts[draftId]
      if (!draft) {
        return state
      }
      return {
        drafts: { ...state.drafts, [draftId]: { ...draft, ...updates } },
      }
    }),

  removeDraft: (draftId: string) =>
    set((state) => {
      const newDrafts = { ...state.drafts }
      delete newDrafts[draftId]
      return {
        drafts: newDrafts,
      }
    }),
}))
